import PaymentSucceed from "@/components/dialogs/PaymentSucceed";
import Text from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  currentSubscriptionState,
  getCurrentSubscription,
} from "@/store/currentSubscriptionState";
import { listProductsState } from "@/store/listProductsState";
import { Activity, useEffect, useState } from "react";
import { useLocation } from "react-router";
import General from "./components/General";
import Subscription from "./components/Subscription";
import Invoices from "./components/Invoices";
import { getListInvoices, listInvoicesState } from "@/store/listInvoicesState";
import ConfirmationDialog from "@/components/dialogs/ConfirmationDialog";
import {
  resetUnsubscribeSubsbcriptionsState,
  unsubscribeSubsbcriptions,
  unsubscribeSubsbcriptionsState,
} from "@/store/unsubscribeSubsbcriptionsState";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { triggerEvent } from "@/lib/utils";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";

export type View = "general" | "subscriptions" | "invoices";

const SubscriptionsAndPayment = () => {
  const { t } = useTranslation();
  const { data: stripeSubscription, isLoading: stripeSubscriptionIsLoading } =
    currentSubscriptionState();
  const { data: unsubState, isLoading: isLoadingUnsub } =
    unsubscribeSubsbcriptionsState();
  const { data: listProducts, isLoading: listProductsIsLoading } =
    listProductsState();
  const { data: listInvoices, isFetching: isfetchingListInvoices } =
    listInvoicesState();
  const location = useLocation();
  const { sm } = useDetectSizeDevice();

  const [view, setView] = useState<View>("general");
  const [openUnsub, setOpenUnsub] = useState(false);

  const { search } = location;

  const paymentIsSucceed = search.includes("session_id");

  useEffect(() => {
    getListInvoices();
  }, []);

  useEffect(() => {
    if (unsubState) {
      setOpenUnsub(false);
      toast.success(t("unsubscription_processed"), {
        duration: 2000,
      });
      triggerEvent("Subscription Canceled", {
        cancel_source: "in_app",
      });
      getCurrentSubscription();
      resetUnsubscribeSubsbcriptionsState();
    }
  }, [unsubState]);

  return (
    <>
      <ConfirmationDialog
        cancelWording={t("cancel")}
        confirmWording={t("confirm")}
        open={openUnsub}
        title={t("unsubscribe")}
        description={t("confirm_unsubscription")}
        onClose={() => setOpenUnsub(false)}
        onConfirm={() => unsubscribeSubsbcriptions()}
        isDisabled={isLoadingUnsub}
      />

      {stripeSubscription &&
        !stripeSubscription.cancelAtPeriodEnd &&
        listProducts &&
        paymentIsSucceed && (
          <PaymentSucceed
            stripeSubscription={stripeSubscription}
            listProducts={listProducts}
            setView={setView}
          />
        )}

      <div
        style={{
          height: sm
            ? "calc(100dvh - var(--height-header))"
            : "calc(100dvh - var(--height-header) - var(--height-header-not-sm))",
        }}
        className="bg-[#F7F8F9] p-[16px] sm:p-[24px] flex flex-col overflow-y-auto"
      >
        <Text fontSize={24} fontWeight={600}>
          {t("subscription_and_payments")}
        </Text>
        <div className="flex gap-[4px] mt-[16px] mb-[32px]">
          <Button
            onClick={() => setView("general")}
            variant={
              view === "general" ? "secondaryFilled" : "secondaryOutlined"
            }
          >
            <Text fontWeight={600} fontSize={14}>
              {t("general")}
            </Text>
          </Button>
          <Button
            onClick={() => setView("subscriptions")}
            variant={
              view === "subscriptions" ? "secondaryFilled" : "secondaryOutlined"
            }
          >
            <Text fontWeight={600} fontSize={14}>
              {t("subscription")}
            </Text>
          </Button>
          <Button
            onClick={() => setView("invoices")}
            variant={
              view === "invoices" ? "secondaryFilled" : "secondaryOutlined"
            }
          >
            <Text fontWeight={600} fontSize={14}>
              {t("invoices")}
            </Text>
          </Button>
        </div>

        <Activity mode={view === "general" ? "visible" : "hidden"}>
          <General
            setView={setView}
            listInvoices={listInvoices}
            listProducts={listProducts}
            stripeSubscription={stripeSubscription}
            stripeSubscriptionIsLoading={stripeSubscriptionIsLoading}
            listProductsIsLoading={listProductsIsLoading}
          />
        </Activity>
        <Activity mode={view === "subscriptions" ? "visible" : "hidden"}>
          <Subscription
            listProducts={listProducts}
            stripeSubscription={stripeSubscription}
            onOpenUnsubDialog={() => setOpenUnsub(true)}
            stripeSubscriptionIsLoading={stripeSubscriptionIsLoading}
            listProductsIsLoading={listProductsIsLoading}
          />
        </Activity>
        <Activity mode={view === "invoices" ? "visible" : "hidden"}>
          <Invoices
            listInvoices={listInvoices}
            isfetchingListInvoices={isfetchingListInvoices}
          />
        </Activity>
      </div>
    </>
  );
};

export default SubscriptionsAndPayment;
